import { useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { Toaster } from '@/app/components/ui/simple-toaster';
import { ErrorBoundary } from '@/app/components/error-boundary';
import { AuthProvider, MembershipInactiveHandler, useAuth } from '@/contexts/auth-context';
import { BrandingProvider } from '@/contexts/branding-context';
import { ProtectedRoute } from '@/app/components/protected-route';
import { OrganizationSettingsGate } from '@/app/components/organization-settings-gate';
import { AuthLayout } from '@/app/components/layouts/auth-layout';
import { AppLayout } from '@/app/components/layouts/app-layout';
import { LoginPage } from '@/app/pages/auth/login-page';
import { ForgotPasswordPage } from '@/app/pages/auth/forgot-password-page';
import { ResetPasswordPage } from '@/app/pages/auth/reset-password-page';
import { InvitePage } from '@/app/pages/auth/invite-page';
import { ChangePasswordPage } from '@/app/pages/auth/change-password-page';
import { MFAEnrollmentPage } from '@/app/pages/auth/mfa-enrollment-page';
import { SelectOrganizationPage } from '@/app/pages/select-organization-page';
import { PlatformDashboardPage } from '@/app/pages/platform-dashboard-page';
import { DashboardPage } from '@/app/pages/dashboard-page';
import { AssetsPage } from '@/app/pages/assets/assets-page';
import { AssetDetailPage } from '@/app/pages/assets/asset-detail-page';
import { CreateAssetPage } from '@/app/pages/assets/create-asset-page';
import { BatchAddAssetsPage } from '@/app/pages/assets/batch-add-assets-page';
import { BatchInspectPage } from '@/app/pages/inspections/batch-inspect-page';
import { SendGearPage } from '@/app/pages/deployments/send-gear-page';
import { EditAssetPage } from '@/app/pages/assets/edit-asset-page';
import { ImportWizardPage } from '@/app/pages/assets/import-wizard-page';
import { CheckOutPage } from '@/app/pages/deployments/check-out-page';
import { CheckInPage } from '@/app/pages/deployments/check-in-page';
import { InspectionsPage } from '@/app/pages/inspections/inspections-page';
import { CreateInspectionPage } from '@/app/pages/inspections/create-inspection-page';
import { InspectionDetailPage } from '@/app/pages/inspections/inspection-detail-page';
import { EditInspectionPage } from '@/app/pages/inspections/edit-inspection-page';
import { DeploymentsPage } from '@/app/pages/deployments/deployments-page';
import { MaintenancePage } from '@/app/pages/maintenance/maintenance-page';
import { CertificationsPage } from '@/app/pages/certifications/certifications-page';
import { CompliancePage } from '@/app/pages/compliance-page';
import { DocumentsPage, CreateDocumentPage, DocumentDetailPage, AcknowledgePage } from '@/app/pages/compliance/documents';
import { CommunicationsPage, CommunicationDetailPage, CreateCommunicationPage } from '@/app/pages/compliance/communications';
import { UtilizationReportPage } from '@/app/pages/reports/utilization-report-page';
import { InspectionComplianceReportPage } from '@/app/pages/reports/inspection-compliance-report-page';
import { RopeCutTrendsPage } from '@/app/pages/reports/rope-cut-trends-page';
import { GearForecastReportPage } from '@/app/pages/reports/gear-forecast-report-page';
import { SettingsPage } from '@/app/pages/settings/settings-page';
import { UserManagementPage } from '@/app/pages/settings/user-management-page';
import { InvitationsPage } from '@/app/pages/settings/invitations-page';
import { ReservationsPage } from '@/app/pages/reservations-page';
import { AssetTemplatesPage } from '@/app/pages/settings/asset-templates-page';
import { AuditLogsPage } from '@/app/pages/settings/audit-logs-page';
import { ModulesPage } from '@/app/pages/settings/modules-page';
import { SsoSettingsPage } from '@/app/pages/settings/sso-settings-page';
import { IntegrationsSettingsPage } from '@/app/pages/settings/integrations/integrations-settings-page';
import { GearRequestsPage } from '@/app/pages/gear-requests/gear-requests-page';
import { GearRequestDetailPage } from '@/app/pages/gear-requests/gear-request-detail-page';
import { GearRequestReceiptPage } from '@/app/pages/gear-requests/gear-request-receipt-page';
import { GearRequestOpsReportPage } from '@/app/pages/gear-requests/gear-request-ops-report-page';
import { GearRequestTemplatesPage } from '@/app/pages/gear-requests/gear-request-templates-page';
import { GearRequestFromTemplatePage } from '@/app/pages/gear-requests/gear-request-from-template-page';
import { GearBoardPage } from '@/app/pages/gear-requests/gear-board-page';
import { KitsPage } from '@/app/pages/kits/kits-page';
import { KitDetailPage } from '@/app/pages/kits/kit-detail-page';
import { CreateKitPage } from '@/app/pages/kits/create-kit-page';
import { EditKitPage } from '@/app/pages/kits/edit-kit-page';
import { ModuleRoute } from '@/app/components/module-route';
import { ModuleNotEnabledPage } from '@/app/pages/module-not-enabled-page';
import { NotFoundPage } from '@/app/pages/not-found-page';
import { SafetyPage } from '@/app/pages/safety/index';
import { CrewPage } from '@/app/pages/crew/index';
import { ProjectsPage, ResolveOldDailyReportRedirect } from '@/app/pages/projects/index';
import { ClientsPage } from '@/app/pages/clients';
import { VendorsPage } from '@/app/pages/vendors/index';
import { PurchaseOrdersPage } from '@/app/pages/purchase-orders/index';
import { RequisitionsPage } from '@/app/pages/requisitions/index';
import { PURCHASING_MODULE_KEY, REQUISITION_MODULE_KEYS } from '@/lib/purchasing';
import { PayrollPage } from '@/app/pages/payroll/index';
import { ComplyPage } from '@/app/pages/compliance/index';
import { WorkPage } from '@/app/pages/work/index';
import { DailyReportsRoutes } from '@/app/pages/work/daily-reports/daily-reports-routes';
import { SubscriptionPage } from '@/app/pages/settings/billing/subscription-page';
import { PlansPage } from '@/app/pages/settings/billing/plans-page';
import { UpgradePage } from '@/app/pages/settings/upgrade-page';
import { PortalPage } from '@/app/pages/portal/index';
import { PeoplePage } from '@/app/pages/people/index';
import { DocumentsPage as FiledDocumentsPage, CreateDocumentPage as CreateFiledDocumentPage } from '@/app/pages/documents';
import { AlertsPage } from '@/app/pages/alerts/alerts-page';
import { AdminDashboardPage } from '@/app/pages/admin/index';
import { MyProfilePage } from '@/app/pages/profile/my-profile-page';
import { MyDashboardPage } from '@/app/pages/my-dashboard-page';
import { RoleRedirect } from '@/app/components/role-redirect';
import { OnboardingPage } from '@/app/pages/onboarding/onboarding-page';
import { OnboardingRedirect } from '@/app/components/onboarding-redirect';
import { GearModelsPage } from '@/app/pages/platform/gear-models-page';
import { GearModelDetailPage } from '@/app/pages/platform/gear-model-detail-page';
import { CreateGearModelPage } from '@/app/pages/platform/create-gear-model-page';
import { GearModelDuplicatesPage } from '@/app/pages/platform/gear-model-duplicates-page';
import { PlatformAdminRoute } from '@/app/components/platform-admin-route';
import { RoleRoute } from '@/app/components/role-route';
import { GearRoute } from '@/app/components/gear-route';
import { ADMIN_DASHBOARD_ROLES } from '@/hooks/use-permissions';
import { PlatformAdminLayout } from '@/app/components/layouts/platform-admin-layout';
import { PlatformAdminDashboardPage } from '@/app/pages/platform-admin/dashboard-page';
import { PlatformAdminOrganizationsPage } from '@/app/pages/platform-admin/organizations-page';
import { PlatformAdminOrganizationDetailPage } from '@/app/pages/platform-admin/organization-detail-page';
import { PlatformAdminSecurityPage } from '@/app/pages/platform-admin/security-page';
import { ImpersonationBanner } from '@/app/components/impersonation-banner';
import { getAppLoginUrl } from '@/lib/app-url';
import { CreateOrganizationPage } from '@/app/pages/platform-admin/create-organization-page';
import { JSAGuestSignPage } from '@/app/pages/safety/jsas/jsa-guest-sign-page';
import { StaffingBoardPage } from '@/app/pages/staffing-board/staffing-board-page';
import { LocationsListPage } from '@/app/pages/cf-locations/locations-list-page';
import { LocationDetailPage } from '@/app/pages/cf-locations/location-detail-page';
import { DataImportPage } from '@/app/pages/settings/data-import-page';
import { GearLitePage } from '@/app/pages/gear-lite/gear-lite-page';
import { MyJobPacketDetailPage } from '@/app/pages/my-dashboard/job-packet-detail-page';
import { MyTravelItineraryDetailPage } from '@/app/pages/my-dashboard/travel-itinerary-detail-page';
import { RequiredFieldValidation } from '@/app/components/required-field-validation';

function LegacyJobsRedirect() {
  const location = useLocation();
  const pathname = location.pathname;
  const search = location.search;

  let to = '/projects/list';
  if (pathname.startsWith('/jobs/')) {
    const rest = pathname.slice(6);
    const [id, ...sub] = rest.split('/');
    if (/^\d+$/.test(id)) {
      const suffix = sub.join('/');
      if (suffix === 'jsas') {
        to = `/projects/${id}/jsas`;
      } else {
        to = `/projects/${id}`;
      }
    } else if (id === 'new') {
      to = '/projects/new';
    }
  } else if (pathname === '/jobs') {
    to = '/projects/list';
  }

  return <Navigate to={to + search} replace />;
}

/**
 * A job is a foundation record: the job list (/projects/list), job creation
 * (/projects/new), the job detail page (/projects/<id>) and its JSAs page
 * (/projects/<id>/jsas) work in every module combination — a Gear-only company can create and
 * manage basic jobs for Send gear, and a Safety-only company can open a
 * job's JSAs. Everything else under /projects stays gated on H3Books.
 */
function FoundationJobRoute({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  if (/^\/projects\/(list|new|\d+(\/jsas)?)\/?$/.test(pathname)) {
    return <>{children}</>;
  }
  return <ModuleRoute moduleKey="projects">{children}</ModuleRoute>;
}

/**
 * Public logout route: clears the session and sends the browser to the app
 * login page. This gives a stuck platform-admin session a direct path back
 * to a clean app.h3ight.com login state.
 */
function LogoutRedirect() {
  const { logout } = useAuth();
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;
    // Await the full logout so the server-side request and token clearing
    // complete before we navigate away.
    logout().then((ok) => {
      if (ok) window.location.href = getAppLoginUrl();
    });
  }, [logout]);

  return (
    <div className="flex h-screen items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
        <p className="text-sm text-gray-500">Logging out...</p>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
    <AuthProvider>
      <BrowserRouter>
        <MembershipInactiveHandler />
        <RequiredFieldValidation />
        <ImpersonationBanner />
        <Routes>
          {/* Auth Routes */}
          <Route element={<AuthLayout />}>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password/:uid/:token" element={<ResetPasswordPage />} />
            <Route path="/invite/:token" element={<InvitePage />} />
            <Route path="/change-password" element={<ChangePasswordPage />} />
            <Route path="/enroll-mfa" element={<MFAEnrollmentPage />} />
          </Route>

          {/* Public Portal Routes (no auth required) */}
          <Route path="/portal/*" element={<PortalPage />} />

          {/* Public JSA Guest Signing (no auth required) */}
          <Route path="/jsa-guest-sign/:token" element={<JSAGuestSignPage />} />

          {/* Public logout route */}
          <Route path="/logout" element={<LogoutRedirect />} />

          {/* Platform Admin Routes (staff only, separate layout) */}
          <Route
            element={
              <PlatformAdminRoute>
                <PlatformAdminLayout />
              </PlatformAdminRoute>
            }
          >
            <Route path="/platform-admin" element={<PlatformAdminDashboardPage />} />
            <Route path="/platform-admin/organizations" element={<PlatformAdminOrganizationsPage />} />
            <Route path="/platform-admin/organizations/new" element={<CreateOrganizationPage />} />
            <Route path="/platform-admin/organizations/:id" element={<PlatformAdminOrganizationDetailPage />} />
            <Route path="/platform-admin/security" element={<PlatformAdminSecurityPage />} />
            <Route path="/platform-admin/*" element={<Navigate to="/platform-admin" replace />} />

            {/* Platform staff gear model management */}
            <Route path="/platform/gear-models" element={<GearModelsPage />} />
            <Route path="/platform/gear-models/new" element={<CreateGearModelPage />} />
            <Route path="/platform/gear-models/custom" element={<GearModelDuplicatesPage />} />
            <Route path="/platform/gear-models/:id" element={<GearModelDetailPage />} />
          </Route>

          {/* Protected Routes */}
          {/* Onboarding — protected but outside AppLayout (full-page wizard) */}
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute>
                <BrandingProvider>
                  <OnboardingPage />
                </BrandingProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path="/select-organization"
            element={
              <ProtectedRoute skipLegalGate>
                <SelectOrganizationPage />
              </ProtectedRoute>
            }
          />

          <Route
            element={
              <ProtectedRoute>
                <OrganizationSettingsGate>
                  <BrandingProvider>
                    <OnboardingRedirect>
                      <AppLayout />
                    </OnboardingRedirect>
                  </BrandingProvider>
                </OrganizationSettingsGate>
              </ProtectedRoute>
            }
          >
            <Route path="/" element={<RoleRedirect />} />
            <Route
              path="/dashboard"
              element={
                <RoleRoute roles={ADMIN_DASHBOARD_ROLES}>
                  <PlatformDashboardPage />
                </RoleRoute>
              }
            />
            <Route path="/alerts" element={<AlertsPage />} />
            <Route path="/documents" element={<FiledDocumentsPage />} />
            <Route path="/documents/new" element={<CreateFiledDocumentPage />} />

            {/* H3Gear Routes (inventory module gated) */}
            <Route element={<ModuleRoute moduleKey="inventory"><Outlet /></ModuleRoute>}>
              {/* H3Gear Dashboard */}
              <Route path="/gear" element={<DashboardPage />} />

              {/* Assets */}
              <Route path="/assets" element={<AssetsPage />} />
              <Route path="/assets/new" element={<GearRoute require="manage"><CreateAssetPage /></GearRoute>} />
              <Route path="/assets/batch" element={<GearRoute require="manage"><BatchAddAssetsPage /></GearRoute>} />
              <Route path="/assets/import" element={<RoleRoute roles={['Admin']}><ImportWizardPage /></RoleRoute>} />
              <Route path="/assets/:id" element={<AssetDetailPage />} />
              <Route path="/assets/:id/edit" element={<GearRoute require="manage"><EditAssetPage /></GearRoute>} />

              {/* Deployments */}
              <Route path="/deployments" element={<DeploymentsPage />} />
              <Route path="/deployments/check-out" element={<GearRoute require="manage"><CheckOutPage /></GearRoute>} />
              <Route path="/deployments/send-gear" element={<GearRoute require="manage"><SendGearPage /></GearRoute>} />
              <Route path="/deployments/check-in" element={<GearRoute require="manage"><CheckInPage /></GearRoute>} />

              {/* Inspections */}
              <Route path="/inspections" element={<InspectionsPage />} />
              <Route path="/inspections/new" element={<GearRoute require="inspect"><CreateInspectionPage /></GearRoute>} />
              <Route path="/inspections/batch" element={<GearRoute require="inspect"><BatchInspectPage /></GearRoute>} />
              <Route path="/inspections/:id" element={<InspectionDetailPage />} />
              <Route path="/inspections/:id/edit" element={<EditInspectionPage />} />

              {/* Maintenance */}
              <Route path="/maintenance" element={<MaintenancePage />} />

              {/* Certifications */}
              <Route path="/certifications" element={<CertificationsPage />} />

              {/* Gear Compliance (inventory-level: expired certs, overdue inspections) */}
              <Route path="/gear/compliance" element={<CompliancePage />} />

              {/* Reports */}
              <Route path="/reports/utilization" element={<UtilizationReportPage />} />
              <Route path="/reports/inspection-compliance" element={<InspectionComplianceReportPage />} />
              <Route path="/reports/rope-cuts" element={<RopeCutTrendsPage />} />
              <Route path="/reports/gear-forecast" element={<GearForecastReportPage />} />

              {/* Reservations */}
              <Route path="/reservations" element={<ReservationsPage />} />

              {/* Kits */}
              <Route path="/kits" element={<KitsPage />} />
              <Route path="/kits/new" element={<CreateKitPage />} />
              <Route path="/kits/:id" element={<KitDetailPage />} />
              <Route path="/kits/:id/edit" element={<EditKitPage />} />

              {/* Gear Requests (G03 fulfillment queue / GB06 board) */}
              <Route path="/gear-requests" element={<GearRequestsPage />} />
              <Route path="/gear-requests/board" element={<GearBoardPage />} />
              <Route path="/gear-requests/ops-report" element={<GearRequestOpsReportPage />} />
              <Route path="/gear-requests/templates" element={<GearRequestTemplatesPage />} />
              <Route path="/gear-requests/templates/:id/use" element={<GearRequestFromTemplatePage />} />
              <Route path="/gear-requests/:id" element={<GearRequestDetailPage />} />
              <Route path="/gear-requests/:id/receive" element={<GearRequestReceiptPage />} />
            </Route>

            {/* Settings (always available, not module-gated) */}
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/settings/users" element={<UserManagementPage />} />
            <Route path="/settings/invitations" element={<InvitationsPage />} />
            <Route path="/settings/templates" element={<ModuleRoute moduleKey="inventory"><AssetTemplatesPage /></ModuleRoute>} />
            <Route path="/settings/audit-logs" element={<AuditLogsPage />} />
            <Route path="/settings/modules" element={<ModulesPage />} />
            <Route path="/settings/sso" element={<SsoSettingsPage />} />
            <Route path="/settings/integrations" element={<IntegrationsSettingsPage />} />
            <Route path="/settings/subscription" element={<SubscriptionPage />} />
            <Route path="/settings/subscription/plans" element={<PlansPage />} />
            <Route path="/settings/upgrade" element={<UpgradePage />} />
            <Route path="/settings/data-import" element={<DataImportPage />} />

            {/* Employee Self-Service Dashboard */}
            <Route path="/my-dashboard" element={<MyDashboardPage />} />
            <Route path="/my-dashboard/job-packets/:id" element={<MyJobPacketDetailPage />} />
            <Route path="/my-dashboard/travel/:id" element={<MyTravelItineraryDetailPage />} />

            {/* Self-Service Profile */}
            <Route path="/profile" element={<MyProfilePage />} />

            {/* Admin Dashboard (always available, Admin/Manager only) */}
            <Route path="/admin" element={<AdminDashboardPage />} />

            {/* H3ightboard — always available, no module gate */}
            <Route path="/h3ightboard" element={<StaffingBoardPage />} />
            <Route path="/staffing-board" element={<Navigate to="/h3ightboard" replace />} />

            {/* People Directory (always available, no module gate) */}
            <Route path="/people/*" element={<PeoplePage />} />

            {/* Core Foundation Locations (always available, no module gate) */}
            <Route path="/locations" element={<LocationsListPage />} />
            <Route path="/locations/:id" element={<LocationDetailPage />} />
            <Route path="/gear-lite" element={<GearLitePage />} />

            {/* Legacy /jobs URLs redirect to /projects */}
            <Route path="/jobs/*" element={<LegacyJobsRedirect />} />

            {/* Clients — always available, no module gate */}
            <Route path="/clients/*" element={<ClientsPage />} />

            {/* Requisitions — reachable with H3Gear or purchasing (see lib/purchasing.ts) */}
            <Route path="/requisitions/*" element={
              <ModuleRoute moduleKey={REQUISITION_MODULE_KEYS}>
                <RequisitionsPage />
              </ModuleRoute>
            } />

            {/* Purchasing: Vendors (S16) and Purchase Orders (S17) — gated by PURCHASING_MODULE_KEY */}
            <Route path="/vendors/*" element={
              <ModuleRoute moduleKey={PURCHASING_MODULE_KEY}>
                <VendorsPage />
              </ModuleRoute>
            } />
            <Route path="/purchase-orders/*" element={
              <ModuleRoute moduleKey={PURCHASING_MODULE_KEY}>
                <PurchaseOrdersPage />
              </ModuleRoute>
            } />
            {/* Module Not Enabled */}
            <Route path="/module-not-enabled" element={<ModuleNotEnabledPage />} />

            {/* Platform Module Routes */}
            <Route path="/safety/*" element={
              <ModuleRoute moduleKey="safety">
                <SafetyPage />
              </ModuleRoute>
            } />
            <Route path="/crew/*" element={
              <ModuleRoute moduleKey="crew">
                <CrewPage />
              </ModuleRoute>
            } />
            {/* Legacy /projects/daily-reports/:id deep links — resolve to /work/daily-reports/:projectId/:id
                and remain reachable when either H3Books or H3Project is enabled. */}
            <Route path="/projects/daily-reports/:id/*" element={
              <ModuleRoute moduleKey={['projects', 'work']}>
                <ResolveOldDailyReportRedirect />
              </ModuleRoute>
            } />
            {/* A job is a foundation record: /projects/list, /projects/new,
                /projects/:id and /projects/:id/jsas work in every module
                combination; the rest
                of /projects stays gated on H3Books (see FoundationJobRoute). */}
            <Route path="/projects/*" element={
              <FoundationJobRoute>
                <ProjectsPage />
              </FoundationJobRoute>
            } />
            <Route path="/payroll/*" element={
              <ModuleRoute moduleKey="payroll">
                <PayrollPage />
              </ModuleRoute>
            } />
            <Route path="/work/daily-reports/*" element={
              <ModuleRoute moduleKey={['projects', 'work']}>
                <DailyReportsRoutes />
              </ModuleRoute>
            } />
            <Route path="/work/*" element={
              <ModuleRoute moduleKey="work">
                <WorkPage />
              </ModuleRoute>
            } />
            {/* Document Control (compliance-gated, specific routes before wildcard) */}
            <Route element={<ModuleRoute moduleKey="compliance"><Outlet /></ModuleRoute>}>
              <Route path="/compliance/documents" element={<DocumentsPage />} />
              <Route path="/compliance/documents/new" element={<CreateDocumentPage />} />
              <Route path="/compliance/documents/:id/edit" element={<CreateDocumentPage />} />
              <Route path="/compliance/documents/:id" element={<DocumentDetailPage />} />
              <Route path="/compliance/documents/:id/acknowledge" element={<AcknowledgePage />} />
              <Route path="/compliance/communications" element={<CommunicationsPage />} />
              <Route path="/compliance/communications/new" element={<CreateCommunicationPage />} />
              <Route path="/compliance/communications/:id" element={<CommunicationDetailPage />} />
            </Route>
            <Route path="/compliance/*" element={
              <ModuleRoute moduleKey="compliance">
                <ComplyPage />
              </ModuleRoute>
            } />

            {/* Catch-all (Wave 6 F2) — must stay last inside AppLayout so an
                unknown URL renders a clear "not found" view inside the app
                shell instead of a blank white page. */}
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster />
    </AuthProvider>
    </ErrorBoundary>
  );
}
